/**
 * The window as a strip of status cells - the "was it up while I was away" view.
 *
 * Severity is encoded **twice**: by hue and by cell height. That is not decoration.
 * A strip of 4px cells cannot carry an icon and a label, so hue would otherwise be
 * the only channel, which fails for a colour-blind reader and in greyscale print.
 * Height gives the same information a second way - a taller cell is a worse one - and
 * the shape of an outage stays visible with colour removed entirely.
 *
 * Hover is nearest-cell rather than per-cell, so the pointer only has to be closest
 * in x. At these widths a per-cell target would be a pinpoint.
 *
 * A cell answers at two depths, because "when did it break" and "what broke" are
 * different questions asked seconds apart. Hovering names the failure in one line -
 * enough to tell one incident from another while sweeping the strip. Clicking opens
 * the sample in full: the error, every failing check with the detail it wrote, and
 * the issues the payload reported. Without the second depth the strip can only ever
 * say that something was wrong, which sends the reader to a log that has usually
 * rotated by the time they look.
 */

import { Fragment, useCallback, useMemo, useRef, useState } from 'react'

import type { HistorySample, Status } from '../../api/types'
import { cn } from '../../lib/cn'
import { formatDateTime, formatMs, formatTime } from '../../lib/format'
import { describeSample } from '../../lib/sample'
import { STATUS_PRESENTATION, statusFill, statusText } from '../../lib/status'
import { Modal } from '../ui/Modal'

/** Cell height as a fraction of the track, worst = tallest. */
const SEVERITY_HEIGHT: Record<Status, number> = {
  healthy: 0.4,
  degraded: 0.65,
  unhealthy: 0.85,
  unreachable: 1,
}

interface StatusTimelineProps {
  samplesInput: HistorySample[]
  height?: number
  className?: string
  /** Caption under the strip, e.g. the window span. */
  caption?: string
  /**
   * Most recent cells to draw. Past a few hundred, cells thinner than ~2px stop
   * carrying their height difference, so the strip shows the newest `maxCells` and
   * says how many it dropped rather than rendering an illegible smear the width of
   * the page. Overflow is also clipped, so even below the cap nothing escapes the card.
   */
  maxCells?: number
  /** Named in the dialog's subtitle, so a detached screenshot says what it is of. */
  label?: string
}

export function StatusTimeline({
  samplesInput,
  height = 26,
  className,
  caption,
  maxCells = 240,
  label,
}: StatusTimelineProps) {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  // Separate from `hoverIndex`: opening the dialog moves focus into it, which blurs the
  // track and clears the hover. The opened sample has to survive that.
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const dropped = Math.max(0, samplesInput.length - maxCells)
  const samples = dropped > 0 ? samplesInput.slice(-maxCells) : samplesInput

  const counts = useMemo(() => {
    const tally = new Map<Status, number>()
    for (const sample of samples) {
      tally.set(sample.status, (tally.get(sample.status) ?? 0) + 1)
    }
    return tally
  }, [samples])

  /** The cell nearest a pointer position, or null when there is nothing to hit. */
  const indexAt = useCallback(
    (clientX: number) => {
      if (!trackRef.current || samples.length === 0) return null
      const box = trackRef.current.getBoundingClientRect()
      const index = Math.floor(((clientX - box.left) / box.width) * samples.length)
      return Math.min(Math.max(index, 0), samples.length - 1)
    },
    [samples.length],
  )

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => setHoverIndex(indexAt(event.clientX)),
    [indexAt],
  )

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (samples.length === 0) return
      // Enter and Space open whatever the arrows have landed on, so the dialog is
      // reachable without a pointer - the detail is not hover-only.
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        setOpenIndex(hoverIndex ?? samples.length - 1)
        return
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault()
      setHoverIndex((current) => {
        const from = current ?? samples.length - 1
        const next = event.key === 'ArrowLeft' ? from - 1 : from + 1
        return Math.min(Math.max(next, 0), samples.length - 1)
      })
    },
    [samples.length, hoverIndex],
  )

  // From the event rather than from `hoverIndex`: a tap fires no pointermove first, so
  // on a touch screen there is no hover to read and every cell would open the last one.
  const onClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => setOpenIndex(indexAt(event.clientX)),
    [indexAt],
  )

  if (samples.length === 0) {
    return (
      <p className={cn('text-xs text-ink-muted', className)}>
        No samples yet — the first poll fills this in.
      </p>
    )
  }

  const hovered = hoverIndex === null ? null : samples[hoverIndex]
  const hoveredReason = hovered ? describeSample(hovered) : null
  const opened = openIndex === null ? null : samples[openIndex]

  return (
    <div className={cn('relative w-full', className)}>
      <div
        ref={trackRef}
        // 2px of surface between cells: white does the separating, never a border.
        // `overflow-hidden` + `min-w-0` on the flex cells keeps a long window inside the
        // card width - many samples make the cells thinner rather than pushing the
        // strip past the page edge. `flex-nowrap` so they never wrap onto a second row.
        className="flex w-full cursor-pointer flex-nowrap items-end gap-[2px] overflow-hidden outline-none"
        style={{ height }}
        role="button"
        aria-label={
          `Status over the last ${samples.length} samples: ` +
          [...counts.entries()]
            .map(([status, count]) => `${count} ${STATUS_PRESENTATION[status].label.toLowerCase()}`)
            .join(', ') +
          '. Use the arrow keys to move through them, Enter to open one in full.'
        }
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setHoverIndex(null)}
        onFocus={() => setHoverIndex(samples.length - 1)}
        onBlur={() => setHoverIndex(null)}
        onKeyDown={onKeyDown}
        onClick={onClick}
      >
        {samples.map((sample, index) => {
          const presentation = STATUS_PRESENTATION[sample.status]
          return (
            <span
              key={`${sample.t}-${index}`}
              aria-hidden
              className="min-w-0 flex-1 rounded-sm transition-opacity"
              style={{
                height: `${SEVERITY_HEIGHT[sample.status] * 100}%`,
                backgroundColor: statusFill(presentation.token),
                // The hovered cell lifts, so the reader sees the strip respond.
                opacity: hoverIndex === null || hoverIndex === index ? 1 : 0.45,
              }}
            />
          )
        })}
      </div>

      {hovered ? (
        <div className="card pointer-events-none absolute -top-2 right-0 z-20 w-max max-w-[20rem] -translate-y-full px-2.5 py-1.5 text-xs shadow-lg">
          <div>
            <span
              className={cn(
                'font-semibold',
                statusText(STATUS_PRESENTATION[hovered.status].token),
              )}
            >
              {STATUS_PRESENTATION[hovered.status].label}
            </span>{' '}
            <span className="text-ink-secondary">
              {formatTime(hovered.t)}
              {hovered.http === null ? '' : ` · HTTP ${hovered.http}`}
            </span>
          </div>
          {hoveredReason?.headline ? (
            <>
              {/* Clamped to two lines: the strip is a few pixels tall and a tooltip
                  that grows to fit a stack trace covers the thing being pointed at. */}
              <p className="mt-1 line-clamp-2 break-words text-ink-secondary">
                {hoveredReason.headline}
              </p>
              <p className="mt-1 text-[10px] text-ink-muted">Click for the full detail</p>
            </>
          ) : null}
        </div>
      ) : null}

      {caption || dropped > 0 ? (
        <p className="mt-1.5 text-[11px] text-ink-muted">
          {caption}
          {dropped > 0 ? `${caption ? ' · ' : ''}showing the newest ${samples.length}` : ''}
        </p>
      ) : null}

      <SampleDialog
        sample={opened}
        label={label}
        onClose={() => setOpenIndex(null)}
      />
    </div>
  )
}

/**
 * One sample, in full.
 *
 * Ordered by how a reader triages: the headline state, then the error if the probe
 * never landed, then each failing check with what it said, then anything the payload
 * raised that no check accounted for. Timings come last - they matter once you know
 * what broke, and never before.
 */
function SampleDialog({
  sample,
  label,
  onClose,
}: {
  sample: HistorySample | null
  label?: string
  onClose: () => void
}) {
  if (!sample) return null

  const description = describeSample(sample)
  const presentation = STATUS_PRESENTATION[sample.status]
  const Icon = presentation.icon
  const latencies = Object.entries(sample.svc ?? {}).sort(([a], [b]) => a.localeCompare(b))

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Icon size={15} aria-hidden className={statusText(presentation.token)} />
          <span className={statusText(presentation.token)}>{presentation.label}</span>
          <span className="font-normal text-ink-secondary">· {formatDateTime(sample.t)}</span>
        </span>
      }
      subtitle={label}
    >
      <div className="space-y-4 text-xs">
        <p className="text-ink-secondary">{presentation.description}</p>

        {description.error ? (
          <section>
            <h3 className="mb-1 font-semibold text-ink">Error</h3>
            <p className="rounded-md border border-critical/30 bg-critical/5 px-2.5 py-2 break-words text-ink">
              {description.error}
            </p>
          </section>
        ) : null}

        {description.failing.length > 0 ? (
          <section>
            <h3 className="mb-1.5 font-semibold text-ink">
              {description.failing.length} failing{' '}
              {description.failing.length === 1 ? 'check' : 'checks'}
            </h3>
            <ul className="space-y-2">
              {description.failing.map((check) => (
                <li key={check.name} className="rounded-md border px-2.5 py-2">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium text-critical">{check.name}</span>
                    <span className="tabular shrink-0 text-ink-muted">
                      {formatMs(check.latencyMs)}
                    </span>
                  </div>
                  <p className="mt-1 break-words text-ink-secondary">
                    {check.detail ?? (
                      <span className="text-ink-muted">
                        Reported down with no detail — the detail is only published on
                        the authenticated view, so this sample was taken without a key.
                      </span>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {description.issues.length > 0 ? (
          <section>
            <h3 className="mb-1.5 font-semibold text-ink">Reported issues</h3>
            <ul className="space-y-2">
              {description.issues.map((issue) => (
                <li key={issue.name} className="rounded-md border px-2.5 py-2">
                  <span className="font-medium text-warning">{issue.name}</span>
                  <p className="mt-1 break-words text-ink-secondary">{issue.detail}</p>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {!description.hasReason ? (
          <p className="rounded-md border px-2.5 py-2 text-ink-secondary">
            {sample.status === 'healthy'
              ? 'Every check passed on this poll.'
              : 'No reason was recorded for this sample. Samples taken before the ' +
                'dashboard started keeping failure detail carry only the status.'}
          </p>
        ) : null}

        <section>
          <h3 className="mb-1.5 font-semibold text-ink">Timing</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
            <dt className="text-ink-muted">Round trip</dt>
            <dd className="tabular text-right text-ink">{formatMs(sample.ms)}</dd>
            <dt className="text-ink-muted">HTTP</dt>
            <dd className="tabular text-right text-ink">{sample.http ?? '—'}</dd>
            {latencies.map(([name, ms]) => (
              <Fragment key={name}>
                <dt className="truncate text-ink-muted">{name}</dt>
                <dd className="tabular text-right text-ink">{formatMs(ms)}</dd>
              </Fragment>
            ))}
          </dl>
        </section>
      </div>
    </Modal>
  )
}
